import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { api, hasRefreshSession, refreshSession, type UserSession } from '@/api/client'

export const useSessionStore = defineStore('session', () => {
  const session = ref<UserSession | null>(null)
  const initialized = ref(false)
  const isAuthenticated = computed(() => Boolean(session.value?.access_token))

  async function bootstrap(): Promise<void> {
    if (initialized.value) return
    if (!hasRefreshSession()) {
      initialized.value = true
      return
    }
    try { session.value = await refreshSession() } catch { session.value = null }
    initialized.value = true
  }

  async function login(organization_slug: string, email: string, password: string): Promise<void> {
    session.value = await api<UserSession>('/api/v1/auth/login', {
      method: 'POST', body: JSON.stringify({ organization_slug, email, password }),
    })
    initialized.value = true
  }

  async function logout(): Promise<void> {
    try { await api<void>('/api/v1/auth/logout', { method: 'POST' }) } finally { session.value = null }
  }

  async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
    if (!session.value) throw new Error('Not authenticated')
    try { return await api<T>(path, options, session.value.access_token) }
    catch (error) {
      if (!(error instanceof Error) || !('status' in error) || error.status !== 401) throw error
      session.value = await refreshSession()
      return api<T>(path, options, session.value.access_token)
    }
  }

  return { session, initialized, isAuthenticated, bootstrap, login, logout, request }
})
